import React, { useState } from 'react';
import {
  Box,
  Checkbox,
  FormHelperText,
  Switch,
  Typography
} from '@mui/material';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import HelpRoundedIcon from '@mui/icons-material/HelpRounded';
import { DesignMode, getDesignTokens } from './designTokens';
import { DesignSurface } from './DesignSurface';
import { SiteButton } from './SiteButton';

type CrystalFormProps = {
  mode?: DesignMode;
};

type AppleTextFieldProps = {
  label: string;
  value?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  error?: boolean;
  disabled?: boolean;
};

type AppleSelectFieldProps = {
  label: string;
  value: string;
  onChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  options: { value: string; label: string }[];
};

const appleFieldBaseSx = (
  mode: DesignMode,
  hasError = false,
  isFocused = false,
  disabled = false
) => {
  const isLight = mode === 'light';

  return {
    position: 'relative',
    minHeight: 62,
    borderRadius: 2.15,
    border: '1.4px solid',
    borderColor: hasError
      ? '#ff3b30'
      : isFocused
        ? '#0071e3'
        : isLight
          ? '#86868b'
          : 'rgba(255,255,255,0.24)',
    background: disabled
      ? isLight
        ? '#f5f5f7'
        : 'rgba(255,255,255,0.035)'
      : isLight
        ? '#ffffff'
        : 'rgba(255,255,255,0.06)',
    boxShadow: isFocused
      ? hasError
        ? '0 0 0 3px rgba(255,59,48,0.12)'
        : '0 0 0 3px rgba(0,113,227,0.14)'
      : 'none',
    transition:
      'border-color 180ms ease, box-shadow 180ms ease, background 180ms ease'
  };
};

const AppleTextField = ({
  label,
  value = '',
  onChange,
  error = false,
  disabled = false,
  mode
}: AppleTextFieldProps & { mode: DesignMode }) => {
  const [isFocused, setIsFocused] = useState(false);
  const isActive = isFocused || Boolean(value);
  const isLight = mode === 'light';

  return (
    <Box sx={appleFieldBaseSx(mode, error, isFocused, disabled)}>
      <Typography
        component="label"
        sx={{
          position: 'absolute',
          left: 20,
          top: isActive ? 9 : '50%',
          transform: isActive ? 'translateY(0)' : 'translateY(-50%)',
          color: error ? '#ff3b30' : isLight ? '#6e6e73' : '#aeb8c7',
          fontSize: isActive ? 14 : 21,
          fontWeight: isActive ? 700 : 500,
          lineHeight: 1,
          pointerEvents: 'none',
          transition:
            'top 180ms ease, transform 180ms ease, font-size 180ms ease, color 180ms ease'
        }}
      >
        {label}
      </Typography>
      <Box
        component="input"
        value={value}
        disabled={disabled}
        onChange={onChange}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        sx={{
          width: '100%',
          height: 60,
          boxSizing: 'border-box',
          border: 0,
          outline: 0,
          background: 'transparent',
          color: isLight ? '#1d1d1f' : '#f7fbff',
          fontSize: 21,
          fontWeight: 600,
          lineHeight: 1.2,
          px: 2.5,
          pt: isActive ? 2.7 : 0,
          opacity: isActive ? 1 : 0,
          transition: 'opacity 140ms ease, padding-top 180ms ease'
        }}
      />
    </Box>
  );
};

const AppleSelectField = ({
  label,
  value,
  onChange,
  options,
  mode
}: AppleSelectFieldProps & { mode: DesignMode }) => {
  const [isFocused, setIsFocused] = useState(false);
  const isLight = mode === 'light';

  return (
    <Box sx={appleFieldBaseSx(mode, false, isFocused)}>
      <Typography
        component="label"
        sx={{
          position: 'absolute',
          left: 20,
          top: 9,
          color: isLight ? '#6e6e73' : '#aeb8c7',
          fontSize: 14,
          fontWeight: 700,
          lineHeight: 1,
          pointerEvents: 'none',
          transition: 'color 180ms ease'
        }}
      >
        {label}
      </Typography>
      <Box
        component="select"
        value={value}
        onChange={onChange}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        sx={{
          width: '100%',
          height: 60,
          border: 0,
          outline: 0,
          appearance: 'none',
          background: 'transparent',
          color: isLight ? '#1d1d1f' : '#f7fbff',
          fontSize: 21,
          fontWeight: 600,
          lineHeight: 1.2,
          px: 2.5,
          pt: 2.7,
          cursor: 'pointer'
        }}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Box>
      <Typography
        sx={{
          position: 'absolute',
          right: 20,
          top: '50%',
          transform: 'translateY(-48%) rotate(90deg)',
          color: isLight ? '#6e6e73' : '#aeb8c7',
          fontSize: 24,
          pointerEvents: 'none'
        }}
      >
        ›
      </Typography>
    </Box>
  );
};

const errorTextSx = {
  mt: 0.8,
  display: 'flex',
  alignItems: 'center',
  gap: 0.7,
  color: '#ff3b30',
  fontSize: 15,
  fontWeight: 650
};

const appleSwitchSx = {
  width: 58,
  height: 34,
  p: 0,
  '& .MuiSwitch-switchBase': {
    p: 0.25,
    transition: 'transform 320ms cubic-bezier(0.22, 1, 0.36, 1)',
    '&.Mui-checked': {
      transform: 'translateX(24px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        opacity: 1,
        backgroundColor: '#20b26b'
      }
    }
  },
  '& .MuiSwitch-thumb': {
    width: 30,
    height: 30,
    boxShadow: '0 2px 7px rgba(0,0,0,0.28)',
    transition:
      'width 180ms ease, box-shadow 180ms ease, transform 320ms cubic-bezier(0.22, 1, 0.36, 1)'
  },
  '& .MuiSwitch-track': {
    borderRadius: 999,
    opacity: 1,
    backgroundColor: '#d2d2d7',
    transition: 'background-color 320ms cubic-bezier(0.22, 1, 0.36, 1)'
  }
};

export const CrystalForm = ({ mode = 'light' }: CrystalFormProps) => {
  const tokens = getDesignTokens(mode);
  const isLight = mode === 'light';
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [street, setStreet] = useState('');
  const [suburb, setSuburb] = useState('Melbourne');
  const [state, setState] = useState('vic');
  const [postcode, setPostcode] = useState('3000');
  const [billing, setBilling] = useState('personal');
  const [businessAddress, setBusinessAddress] = useState(false);
  const [terms, setTerms] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const firstNameError = submitted && !firstName.trim();
  const streetError = submitted && street.trim().length > 35;
  const termsError = submitted && !terms;

  const handleSubmit = () => {
    setSubmitted(true);
  };

  return (
    <DesignSurface
      mode={mode}
      elevated
      sx={{
        p: { xs: 2.2, sm: 3.5, md: 4.5 },
        background: isLight ? '#ffffff' : undefined
      }}
    >
      <Typography
        sx={{
          color: tokens.colors.text,
          fontSize: { xs: 30, md: 42 },
          fontWeight: 850,
          lineHeight: 1.08,
          letterSpacing: '-0.03em',
          mb: 3
        }}
      >
        Enter your name and address:
      </Typography>

      <Box sx={{ display: 'grid', gap: 1.9 }}>
        <Box>
          <AppleTextField
            mode={mode}
            label="First Name"
            value={firstName}
            onChange={(event) => setFirstName(event.target.value)}
            error={firstNameError}
          />
          {firstNameError && (
            <Typography sx={errorTextSx}>
              <ErrorOutlineRoundedIcon sx={{ fontSize: 18 }} />
              Please enter a first name.
            </Typography>
          )}
        </Box>

        <AppleTextField
          mode={mode}
          label="Last Name"
          value={lastName}
          onChange={(event) => setLastName(event.target.value)}
        />

        <Box>
          <AppleTextField
            mode={mode}
            label="Street Address"
            value={street}
            onChange={(event) => setStreet(event.target.value)}
            error={streetError}
          />
          {streetError && (
            <Typography sx={errorTextSx}>
              <ErrorOutlineRoundedIcon sx={{ fontSize: 18 }} />
              Please enter a maximum of 35 characters.
            </Typography>
          )}
        </Box>

        <AppleTextField
          mode={mode}
          label="Additional Address Info (optional)"
        />

        <AppleTextField
          mode={mode}
          label="Suburb"
          value={suburb}
          onChange={(event) => setSuburb(event.target.value)}
        />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2
          }}
        >
          <AppleSelectField
            mode={mode}
            label="State"
            value={state}
            onChange={(event) => setState(event.target.value)}
            options={[
              { value: 'vic', label: 'VIC' },
              { value: 'nsw', label: 'NSW' },
              { value: 'qld', label: 'QLD' },
              { value: 'wa', label: 'WA' }
            ]}
          />

          <AppleTextField
            mode={mode}
            label="Postcode"
            value={postcode}
            onChange={(event) => setPostcode(event.target.value)}
          />
        </Box>

        <AppleTextField
          mode={mode}
          label="Country/Region"
          value="Australia"
          disabled
        />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            p: 0.45,
            borderRadius: 999,
            background: isLight ? '#e8e8ed' : 'rgba(255,255,255,0.08)',
            gap: 0.35
          }}
        >
          {[
            ['personal', 'Personal'],
            ['business', 'Business'],
            ['gift', 'Gift']
          ].map(([value, label]) => {
            const isActive = billing === value;
            return (
              <Box
                key={value}
                component="button"
                type="button"
                onClick={() => setBilling(value)}
                sx={{
                  minHeight: 48,
                  border: 0,
                  borderRadius: 999,
                  background: isActive
                    ? isLight
                      ? '#1d1d1f'
                      : '#f5f5f7'
                    : 'transparent',
                  color: isActive
                    ? isLight
                      ? '#ffffff'
                      : '#1d1d1f'
                    : tokens.colors.text,
                  fontSize: { xs: 15, sm: 18 },
                  fontWeight: 750,
                  cursor: 'pointer',
                  transition:
                    'background 160ms ease, color 160ms ease, transform 160ms ease',
                  '&:hover': {
                    transform: 'translateY(-1px)'
                  }
                }}
              >
                {label}
              </Box>
            );
          })}
        </Box>

        <Box
          sx={{
            display: 'grid',
            gap: 1.8,
            pt: 0.8
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1 }}>
              <Typography
                sx={{
                  color: tokens.colors.text,
                  fontSize: { xs: 18, sm: 22 },
                  fontWeight: 650
                }}
              >
                This is a business address
              </Typography>
              <HelpRoundedIcon
                sx={{ color: tokens.colors.textMuted, fontSize: 24 }}
              />
            </Box>
            <Switch
              checked={businessAddress}
              onChange={(event) => setBusinessAddress(event.target.checked)}
              sx={appleSwitchSx}
            />
          </Box>

          <Box>
            <Box
              component="label"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.2,
                color: tokens.colors.text,
                cursor: 'pointer'
              }}
            >
              <Checkbox
                checked={terms}
                onChange={(event) => setTerms(event.target.checked)}
                sx={{
                  p: 0,
                  color: termsError ? '#ff3b30' : '#86868b',
                  '&.Mui-checked': { color: '#0071e3' },
                  '& .MuiSvgIcon-root': { fontSize: 31 }
                }}
              />
              <Typography
                sx={{ fontSize: { xs: 17, sm: 20 }, fontWeight: 650 }}
              >
                I agree to the terms
              </Typography>
            </Box>
            {termsError && (
              <FormHelperText error sx={{ ml: 0, mt: 0.8, fontWeight: 750 }}>
                You must accept the terms before continuing.
              </FormHelperText>
            )}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          mt: 3.2,
          display: 'flex',
          justifyContent: 'flex-end'
        }}
      >
        <SiteButton mode={mode} onClick={handleSubmit}>
          Validate Form
        </SiteButton>
      </Box>
    </DesignSurface>
  );
};
